import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { ArrowLeft } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, durations } from '../../theme';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useEscapeToClose } from '../../utils/escapeStack';
import { panelHistory } from '../../hooks/usePanelHistory';

/** A view pushed onto a PanelStack — People inside Assign, a checklist item
 *  picker inside the composer, and so on (panel-system.md's "drill-in"). */
export interface PanelStackViewSpec {
  key: string;
  title: string;
  render: () => React.ReactNode;
  /** The element that opened this view (a row, a button). Its focus is
   *  restored when this view is popped, so the user's place in the parent
   *  view isn't lost. */
  triggerRef?: React.RefObject<{ focus?: () => void } | null>;
}

interface PanelStackContextValue {
  push: (view: PanelStackViewSpec) => void;
  pop: () => void;
  popTo: (key: string) => void;
  depth: number;
  topKey: string | null;
}

const PanelStackContext = createContext<PanelStackContextValue | null>(null);

/** Reads the nearest PanelStack's navigation. Throws outside one — a view
 *  that pushes or pops has no sensible fallback if there's no stack to act
 *  on. */
export function usePanelStack(): PanelStackContextValue {
  const ctx = useContext(PanelStackContext);
  if (!ctx) {
    throw new Error('usePanelStack() must be called inside a <PanelStack>.');
  }
  return ctx;
}

interface PanelStackProps {
  /** The base view — never unmounted, and never has a back button. */
  root: React.ReactNode;
  /** Read by nothing today beyond the root's own accessibilityLabel; kept
   *  so a caller can name the root the same way a pushed view names itself
   *  with `title`, for when the root grows its own heading. */
  rootTitle?: string;
  /** Opts this stack into browser history (panel-system.md's "Deep links
   *  and back", Phase 4A): each pushed view registers an entry keyed
   *  `${historyKey}:${view.key}`, and browser Back runs the same pop
   *  Escape already runs for the topmost view. Omit for a PanelStack that
   *  shouldn't touch history — a phone AdaptiveSheet body, say, where the
   *  sheet's own dismissal already owns the back gesture. Two PanelStacks
   *  on screen at once (Assign's People, the structure panel's post
   *  drill-in) need distinct keys — usePanelHistory.ts's module-level
   *  coordinator has no other way to tell their entries apart. */
  historyKey?: string;
  /** Caps each pushed view's header + body at this width and centers it,
   *  matching a caller whose root isn't full-bleed (the composer's
   *  centered form column, panel-system-review-notes.md #12b) — otherwise
   *  a pushed view spans the full host width even when the root doesn't.
   *  Omit for a host that's already the right width on its own (a
   *  DockedPanel/CompanionPanel column, a phone AdaptiveSheet body). */
  columnMaxWidth?: number;
}

/**
 * Push/pop navigation inside any container (panel-system.md's "drill-in"):
 * People inside Assign, the checklist and before-post pickers inside the
 * composer, roles/upgrade inside the tour. The root and every pushed view up
 * to the current top stay mounted for as long as they're in the stack —
 * hidden via `display:'none'`, never unmounted — so typed form state in a
 * view the user backs out of and returns to survives the round trip.
 * Popping plays the push in reverse (slide 20px right, fade, over
 * `durations.quick`) and then removes the view; the view underneath comes
 * back from the left as it goes. While two views overlap, all but the one
 * in the flow are absolutely positioned so they stack instead of splitting
 * the height. A covered view only gets `display:none` once its cover
 * animation settles.
 *
 * Works standalone: nothing needs to wrap `<PanelStack>` itself, and it
 * nests inside `DockedPanel`, `CompanionPanel`, or a phone `AdaptiveSheet`
 * body alike.
 */
export function PanelStack({ root, rootTitle, historyKey, columnMaxWidth }: PanelStackProps) {
  const [stack, setStack] = useState<PanelStackViewSpec[]>([]);
  // Popped views still playing their exit. They render after (on top of) the
  // live stack and are removed by `onGone` when the animation ends. Kept out
  // of `stack` so depth/topKey/Escape reflect the logical stack at once.
  const [leaving, setLeaving] = useState<PanelStackViewSpec[]>([]);
  // Read inside pop/popTo without making their identity depend on `stack`,
  // which would otherwise re-push a new escape-close callback into every
  // mounted PanelStackEntry on every navigation.
  const stackRef = useRef(stack);
  stackRef.current = stack;
  // Stable across renders even though callers rarely change it — a ref
  // rather than a dependency, so push/pop/popTo's identity doesn't churn.
  const historyKeyRef = useRef(historyKey);
  historyKeyRef.current = historyKey;
  const entryKey = (viewKey: string) => `${historyKeyRef.current}:${viewKey}`;

  const focusTrigger = (view: PanelStackViewSpec | undefined) => {
    if (Platform.OS !== 'web' || !view?.triggerRef?.current) return;
    // Deferred a frame: this call runs synchronously inside pop()/popTo(),
    // before React commits the state update that flips the popped-back-to
    // view from `display:none` to visible (see the `stack.length > 0`
    // toggle on `root` and on each PanelStackEntry below) — a still-hidden
    // element can't take focus. rAF runs after that commit paints.
    requestAnimationFrame(() => view.triggerRef?.current?.focus?.());
  };

  // The state-only pop history's popstate callback runs — no history
  // bookkeeping here, so it can't recurse back into itself (see `pop`
  // below, which does that part before calling this).
  const rawPop = useCallback(() => {
    const popped = stackRef.current[stackRef.current.length - 1];
    if (popped) setLeaving((prev) => [...prev.filter((v) => v.key !== popped.key), popped]);
    setStack((prev) => prev.slice(0, -1));
    focusTrigger(popped);
  }, []);

  const onGone = useCallback((key: string) => {
    setLeaving((prev) => prev.filter((v) => v.key !== key));
  }, []);

  const push = useCallback(
    (view: PanelStackViewSpec) => {
      // A view pushed again while its previous copy is still leaving would
      // share a React key with it; drop the old copy.
      setLeaving((prev) => prev.filter((v) => v.key !== view.key));
      setStack((prev) => [...prev, view]);
      if (historyKeyRef.current) {
        panelHistory()?.pushPanelEntry(entryKey(view.key), rawPop);
      }
    },
    [rawPop]
  );

  // The UI-driven pop (Escape, the back arrow, a view's own "Use" action):
  // removes this view's history entry (a guarded `history.back()` — see
  // usePanelHistory.ts) before changing state, then does the real pop.
  const pop = useCallback(() => {
    const popped = stackRef.current[stackRef.current.length - 1];
    if (historyKeyRef.current && popped) {
      panelHistory()?.popPanelEntry(entryKey(popped.key));
    }
    rawPop();
  }, [rawPop]);

  const popTo = useCallback((key: string) => {
    const prev = stackRef.current;
    const index = prev.findIndex((v) => v.key === key);
    if (index === -1) return;
    const poppedTop = prev[prev.length - 1];
    if (historyKeyRef.current) {
      // Deepest first, matching the order PanelStack's own stack unwinds.
      for (let i = prev.length - 1; i > index; i--) {
        panelHistory()?.popPanelEntry(entryKey(prev[i].key));
      }
    }
    setStack(prev.slice(0, index + 1));
    // Only the top view animates out; the covered ones between are already
    // out of sight and just go.
    if (poppedTop.key !== key) {
      setLeaving((l) => [...l.filter((v) => v.key !== poppedTop.key), poppedTop]);
    }
    if (poppedTop.key !== key) focusTrigger(poppedTop);
  }, []);

  const topKey = stack.length > 0 ? stack[stack.length - 1].key : null;

  const contextValue = useMemo<PanelStackContextValue>(
    () => ({ push, pop, popTo, depth: stack.length, topKey }),
    [push, pop, popTo, stack.length, topKey]
  );

  return (
    <PanelStackContext.Provider value={contextValue}>
      <View style={styles.host}>
        <CoverView
          covered={stack.length > 0}
          inFlow={stack.length === 0}
          accessibilityLabel={rootTitle}>
          {root}
        </CoverView>
        {/* One list so a view keeps its React instance when a pop moves it
            from `stack` to `leaving` (a second .map would remount it and
            drop its state and animation). */}
        {[...stack, ...leaving].map((entry, index) => (
          <PanelStackEntry
            key={entry.key}
            entry={entry}
            mode={
              index >= stack.length ? 'leaving' : index === stack.length - 1 ? 'top' : 'covered'
            }
            columnMaxWidth={columnMaxWidth}
            onGone={onGone}
          />
        ))}
      </View>
    </PanelStackContext.Provider>
  );
}

// How far a view travels when it enters or leaves, and how far the view it
// covers slides back. Small on purpose: a hint of direction, not a page turn.
const ENTER_OFFSET = 20;
const COVER_OFFSET = -12;

// Matches theme/motion.ts's `easings.exit`; see DockedPanel.tsx.
const EXIT_EASING = Easing.bezier(0.4, 0, 1, 1);

/**
 * Wraps a view that a push can cover. Covered: slides to COVER_OFFSET and
 * fades out over `durations.base`; uncovered: reverses. `display:none` only
 * applies once the cover animation has settled, so the view is visible
 * throughout the slide, and it comes back the instant it's uncovered (before
 * its animation runs), so a focus() call right after a pop can succeed.
 * `inFlow` false lays the view over its siblings instead of beside them.
 */
function CoverView({
  covered,
  inFlow,
  children,
  testID,
  accessibilityLabel,
}: {
  covered: boolean;
  inFlow: boolean;
  children: React.ReactNode;
  testID?: string;
  accessibilityLabel?: string;
}) {
  const reducedMotion = useReducedMotion();
  const cover = useRef(new Animated.Value(covered ? 1 : 0)).current;
  const [settled, setSettled] = useState(covered);
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setSettled(false);
    const animation = Animated.timing(cover, {
      toValue: covered ? 1 : 0,
      duration: reducedMotion ? 0 : durations.base,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => {
      if (finished && covered) setSettled(true);
    });
    return () => animation.stop();
  }, [covered, reducedMotion, cover]);

  const translateX = cover.interpolate({ inputRange: [0, 1], outputRange: [0, COVER_OFFSET] });
  const opacity = cover.interpolate({ inputRange: [0, 1], outputRange: [1, 0] });

  return (
    <Animated.View
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      aria-hidden={covered}
      pointerEvents={covered ? 'none' : undefined}
      style={[
        inFlow ? styles.visible : styles.overlay,
        covered && settled && styles.hidden,
        { opacity, transform: [{ translateX }] },
      ]}>
      {children}
    </Animated.View>
  );
}

function PanelStackEntry({
  entry,
  mode,
  columnMaxWidth,
  onGone,
}: {
  entry: PanelStackViewSpec;
  mode: 'top' | 'covered' | 'leaving';
  columnMaxWidth?: number;
  onGone: (key: string) => void;
}) {
  const { pop } = usePanelStack();
  const reducedMotion = useReducedMotion();
  const progress = useRef(new Animated.Value(0)).current;
  const headingRef = useRef<Text>(null);
  const leaving = mode === 'leaving';

  // Topmost or not, this view is mounted for as long as it's in the stack —
  // its escape registration follows, so Escape closes whichever pushed view
  // is deepest (escapeStack.ts's LIFO order) regardless of which component
  // instance's `pop` actually ran the shared stack-slice. A leaving view has
  // already been popped, so it must not answer Escape again.
  useEscapeToClose(!leaving, pop);

  // Push in on mount; play back out when the parent flips this to `leaving`.
  // The view stays mounted until onGone, never unmounted mid-animation.
  useEffect(() => {
    const animation = Animated.timing(progress, {
      toValue: leaving ? 0 : 1,
      duration: reducedMotion ? 0 : leaving ? durations.quick : durations.base,
      easing: leaving ? EXIT_EASING : Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => {
      if (finished && leaving) onGone(entry.key);
    });
    return () => animation.stop();
    // reducedMotion changing mid-flight isn't worth restarting a one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // Fires once when this view mounts (a push), not on every re-render —
    // headingRef itself never changes identity, so it needs no deps entry.
    (headingRef.current as unknown as HTMLElement | null)?.focus?.();
  }, []);

  const translateX = progress.interpolate({ inputRange: [0, 1], outputRange: [ENTER_OFFSET, 0] });

  return (
    <CoverView
      testID={`panel-stack-view-${entry.key}`}
      covered={mode === 'covered'}
      inFlow={mode === 'top'}>
      <Animated.View
        pointerEvents={leaving ? 'none' : undefined}
        // data-redi-leaving lets web audits skip a view that is sliding out.
        {...(leaving ? ({ dataSet: { rediLeaving: 'true' } } as object) : null)}
        style={[styles.entryContainer, { opacity: progress, transform: [{ translateX }] }]}>
        <View style={[styles.column, columnMaxWidth != null && { maxWidth: columnMaxWidth }]}>
          <PanelStackView
            title={entry.title}
            onBack={pop}
            headingRef={headingRef}
            headingId={`panel-stack-heading-${entry.key}`}
          />
          <View style={styles.body}>{entry.render()}</View>
        </View>
      </Animated.View>
    </CoverView>
  );
}

interface PanelStackViewProps {
  title: string;
  onBack: () => void;
  headingRef: React.RefObject<Text | null>;
  /** nativeID (renders as the DOM `id` on web) so a companion's own
   *  aria-labelledby, or this stack's own uses, can point at the heading. */
  headingId?: string;
}

/**
 * The back-arrow-plus-heading row PanelStack renders automatically above
 * every pushed view. Exported so a caller building its own drill-in-shaped
 * header (outside PanelStack, e.g. matching its chrome) can reuse it.
 */
export function PanelStackView({ title, onBack, headingRef, headingId }: PanelStackViewProps) {
  const { colors } = useTheme();
  return (
    <View style={styles.header}>
      <Pressable
        onPress={onBack}
        testID="panel-stack-back"
        accessibilityRole="button"
        aria-label="Back"
        hitSlop={8}
        style={styles.backButton}>
        <ArrowLeft size={20} color={colors.text} />
      </Pressable>
      <Text
        ref={headingRef}
        testID="panel-stack-heading"
        nativeID={headingId}
        // Spread, not a prop: tabIndex is only in Text's types when the
        // generated expo-env.d.ts is present, which it isn't in CI.
        {...(Platform.OS === 'web' ? { tabIndex: -1 } : null)}
        style={[styles.heading, { color: colors.text }]}
        numberOfLines={1}>
        {title}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  host: {
    flex: 1,
    minHeight: 0,
  },
  visible: {
    flex: 1,
    minHeight: 0,
  },
  // Kept mounted, just out of flow and out of the accessibility tree — not
  // unmounted, so typed input state survives a push and later a pop back.
  hidden: {
    display: 'none',
  },
  // A view that's mid-transition but not the one in the flow: laid over its
  // siblings so two visible views don't split the host's height.
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  entryContainer: {
    flex: 1,
    minHeight: 0,
  },
  // Centers header + body at columnMaxWidth when the caller passes one —
  // a no-op (full width, no cap) otherwise.
  column: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    alignSelf: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
  },
  backButton: {
    padding: SPACING.xs,
    marginLeft: -SPACING.xs,
  },
  // Focused on push so screen readers announce it (tabIndex -1, never
  // tabbed to), so it needs no visible ring: a white box showed around it
  // after a keyboard-driven open (Sept 28).
  heading: {
    ...TYPOGRAPHY.h4,
    flex: 1,
    outlineStyle: 'none',
  } as object,
  body: {
    flex: 1,
    minHeight: 0,
  },
});
